/**
 * Shared utility for converting File objects to the format expected by Supabase file uploads
 * Used by both chat messages and service request attachments
 */

export interface UploadFileData {
  data: string; // base64 data URL with prefix (e.g., "data:image/jpeg;base64,...")
  originalFilename: string;
  fileSize: number;
  mimeType: string;
  displayOrder: number;
}

/**
 * Convert File objects to the format expected by Supabase file uploads
 * This ensures consistency between chat messages and service request attachments
 */
export const convertFilesToUploadFormat = async (files: File[]): Promise<UploadFileData[]> => {
  const convertedFiles: UploadFileData[] = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    try {
      // Convert file to base64 data URL (includes MIME type prefix)
      const base64DataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      convertedFiles.push({
        data: base64DataUrl, // Full data URL with prefix
        originalFilename: file.name,
        fileSize: file.size,
        mimeType: file.type,
        displayOrder: i + 1
      });
    } catch (error) {
      console.error(`Error converting file ${file.name} to base64:`, error);
      throw new Error(`Failed to process attachment: ${file.name}`);
    }
  }
  
  return convertedFiles;
};

/**
 * Validate file array before conversion
 * Returns validation result with error message if invalid
 */
export const validateFilesForUpload = (files: File[]): { isValid: boolean; error?: string } => {
  if (files.length > 3) {
    return { isValid: false, error: 'Maximum 3 images allowed' };
  }
  
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file
  const MAX_TOTAL_SIZE = 15 * 1024 * 1024; // 15MB total
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  
  let totalSize = 0;
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, error: `File "${file.name}" exceeds 5MB limit` };
    }
    
    // Check file type
    if (!allowedTypes.includes(file.type)) {
      return { isValid: false, error: `File "${file.name}" has unsupported type: ${file.type}` };
    }
    
    totalSize += file.size;
  }
  
  // Check total size
  if (totalSize > MAX_TOTAL_SIZE) {
    return { 
      isValid: false, 
      error: `Total file size (${(totalSize / 1024 / 1024).toFixed(1)}MB) exceeds 15MB limit` 
    };
  }
  
  return { isValid: true };
};